import type { defineComponent } from 'vue';
import { keyBy } from 'lodash-es';
import { register } from '@builder.io/sdk-vue';
import type { BuilderInput, BuilderConfig } from '@/types/builder';
import { builderComponents } from '@/components/builder';
import { flatColors } from '../../../tailwind.utils';

const componentsToRegister: BuilderConfig[] = builderComponents;
const colorTokens = Object.entries(flatColors).map(([name, value]) => ({ name, value }));

register('editor.settings', {
    designTokens: {
        fontFamily: [
            { name: 'Primary Font (Roboto)', value: 'Roboto, sans-serif' },
            { name: 'Secondary Font (Work Sans)', value: 'Work Sans, sans-serif' },
        ],
        colors: colorTokens,
    },
});

function mapVueTypeToBuilderType(propType: string): string {
    if (!propType) return 'string';

    const lowerType = propType.toLowerCase();
    return lowerType === 'array' ? 'list' : lowerType;
}

function mapComponentPropsToBuilderInputs(component: ReturnType<typeof defineComponent>): BuilderInput[] {
    const { props } = component;
    if (!props) return [];

    return Object.keys(props).map((propName) => {
        const prop = props[propName];

        return {
            name: propName,
            type: mapVueTypeToBuilderType(prop.type.name),
            defaultValue: prop.default,
        };
    });
}

// Helper function to merge inputs. Latest inputs override previous (last wins).
function mergeInputs(inputs: BuilderInput[]): BuilderInput[] {
    const inputsByName = keyBy(inputs, (input) => input.name);
    return Object.values(inputsByName);
}

const componentsWithAllProps = componentsToRegister.map((config) => {
    const propInputs = mapComponentPropsToBuilderInputs(config.component);
    return {
        ...config,
        inputs: mergeInputs([...propInputs, ...(config.inputs || [])]),
    };
});

const componentsByName = keyBy(componentsWithAllProps, (config) => config.component.name as string);

function registerBuilderComponent({ component, childComponents, inputs, ...config }: BuilderConfig) {
    const childInputs: BuilderInput[] =
        childComponents?.flatMap((child): BuilderInput[] => {
            const childConfig = child.name ? componentsByName[child.name] : undefined;
            if (!childConfig) {
                return mapComponentPropsToBuilderInputs(child);
            }

            return childConfig.inputs ?? [];
        }) ?? [];

    return {
        type: 'vue',
        ...config,
        component,
        name: config.name ?? component.name,
        inputs: mergeInputs([
            ...(childInputs || []),
            ...(inputs || []),
        ]),
        defaultStyles: {
            display: 'block',
        },
    };
}

export const registeredComponents = componentsToRegister.map(registerBuilderComponent);
